import {
  AiMark,
  type CommandItem,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InlineMessage,
  type ObjectMetadata,
  ObjectRecordPage,
  OptionSelect,
  PageContent,
  RecordFormDialog,
  type RecordFormField,
  RelatedList,
  type RelatedListActions,
  Spinner,
  Stack,
  type SuggestedLink,
  SuggestedLinksSection,
} from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { isDerivable } from '../derived/derivedApi';
import { FromHistorySection } from '../derived/FromHistorySection';
import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { restoreNote } from '../setup/recycleBinApi';
import { usePageActions } from '../pageActions';
import { recordView } from '../search/recentRecords';
import { audienceFor } from '../config/objects/audiences';
import { objects } from '../config/objects';
import { QUOTE_TAB_KEYS, useQuoteWorkspace } from '../quotes/QuoteWorkspace';
import { loadHiddenTabs, saveHiddenTabs } from '../quotes/tabVisibility';
import { ActivityTab } from './ActivityTab';
import { FollowUpPlan } from '../home/FollowUpPlan';
import { browserStorage } from './listState';
import type { ObjectKey } from '../navigation';
import { loadLookupOptions } from './lookupOptions';
import { useMergeHistory } from './mergeHistory';
import { MergedRecordsSection } from './MergedRecordsSection';
import { mergeSpecOf } from './mergeSpecs';
import {
  ObjectsApiError,
  type ObjectMeta,
  objectMetaUrl,
  type ObjectRow,
  parseObjectListResponse,
  parseObjectMetaResponse,
  parseRecordResponse,
  recordUrl,
  relatedUrl,
} from './objectsApi';
import { withSchemaDetails } from './schemaDetails';
import { CLOSED, offeredOutcomes, PATHS, pathOf, proposedValue } from './paths';
import { MergeWithAnother } from './recordMerge';
import { queryParams, type RelatedListQuery, serverSortable } from './relatedQuery';
import { clonable, clonePath, isObjectKey, listPath, recordHref, recordPath, relatedPath } from './routes';
import { contactOptionsFor } from '../opportunities/contactPicker';
import { quoteOptionsFor } from '../opportunities/quotePicker';
import { ContractingTab } from '../opportunities/ContractingTab';
import { DeliveryTab } from '../opportunities/DeliveryTab';
import { loadSummary, type OpportunitySummary as Summary } from '../opportunities/opportunityPageApi';
import { NextStepCard, QuoteLinesCard } from '../opportunities/OpportunityRail';
import { OpportunitySummary } from '../opportunities/OpportunitySummary';
import { RevenuePlanTab } from '../opportunities/RevenuePlanTab';
import { RevenueScheduleEditor } from '../opportunities/RevenueScheduleEditor';
import { TranscriptTab } from '../meetings/TranscriptTab';
import { decideLinkSuggestion, type LinkDecision, LinkSuggestionError, loadLinkSuggestions, toSuggestedLink } from '../meetings/linkSuggestions';
import { StrategyTab, useOpportunityStrategy } from '../opportunities/StrategyTab';
import { reviewGuideline } from '../opportunities/strategyApi';
import { patchScheduleRow } from '../opportunities/scheduleApi';
import { BillingTab, CatalogTab, PagePending, ProductSummary, SalesTab, StructureTab, useProductPage } from '../products/ProductPage';
import { PRODUCT_TABS } from '../config/objects/products';
import { ProductSetupChecklist, useProductReadiness } from '../products/ProductSetupChecklist';
import { OpenDocumentButton } from '../research/ResearchFiles';
import { RecordHistorySection } from '../history/RecordHistorySection';
import { historyShown } from '../history/historyApi';
import { useRecordHistory } from '../history/useRecordHistory';
import { PriceGridCellsEditor } from '../priceGrids/PriceGridCellsEditor';
import { activateProduct, ProductSetupError, retireProduct } from '../products/productSetup';
import {
  addLink,
  createBody,
  cellBody,
  createRecord,
  deleteRecord,
  formFields,
  joinColumns,
  NOT_WRITABLE,
  objectKeyOf,
  parentDefaults,
  patchBody,
  type Relation,
  removeLink,
  toWire,
  updateRecord,
  withEditors,
  type Writable,
} from './writes';

type Load =
  | { state: 'loading' }
  | { state: 'missing' }
  | { state: 'error'; message: string }
  | {
      state: 'ready';
      record: ObjectRow;
      related: Record<string, unknown[]>;
      totals: Record<string, number>;
      /** Lists with more rows than were loaded: the server sorts and filters them. */
      paged: Record<string, boolean>;
      relatedFailed: boolean;
      writable: Writable;
      relations: Record<string, Relation>;
      /** A join list's own writable fields (a contact role's role and primary), by relation key. */
      joinWritable: Record<string, Writable>;
      /** The object's columns and fields from /meta: an object described by the schema lays its Details out from them. */
      schema: Pick<ObjectMeta, 'columns' | 'fields'> | null;
    };

/** A related-list action in progress: a New, Edit or Add dialog, or a Delete or Remove to confirm. */
type RelatedTask =
  | {
      step: 'form';
      title: string;
      fields: RecordFormField[];
      initialValues?: Record<string, string>;
      initialLabels?: Record<string, string>;
      submitLabel?: string;
      submit: (values: Record<string, string | null>) => Promise<void>;
    }
  | { step: 'confirm'; title: string; name: string; verb: 'Delete' | 'Remove'; run: () => Promise<void> };

/** "Members" → "member", for "Add member". */
const singularOf = (label: string) => (label.endsWith('s') ? label.slice(0, -1) : label).toLowerCase();

/** The definition a record renders with: an audience's members follow its entity type. */
export function metadataFor(object: ObjectKey, record: ObjectRow): ObjectMetadata<ObjectRow> {
  return object === 'audiences' ? audienceFor(record) : objects[object];
}

/** What the Closed step says when an opportunity has no Deal Retro (the server's 409 says the same). */
/** A meeting's related lists that a suggested link is written to. */
const MEETING_LINK_LISTS = ['accounts', 'opportunities', 'projects', 'contacts'] as const;

export const DEAL_RETRO_REQUIRED = 'Add a Deal Retro before closing this opportunity.';

const messageOf = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message.length > 0 ? error.message : fallback;

/**
 * One record at /<object>/:id, laid out by its definition in config/objects
 * and rendered by the library's ObjectRecordPage: details in sections, related
 * lists in the rail or in tabs, every record name and lookup a link.
 * `role` is the signed-in person's (`/api/me`): only an Admin is shown an
 * account's or contact's Merged Records (MOTHER-180), so for anyone else they
 * are not asked for.
 */
export function ObjectRecordView({ object, role }: { object: ObjectKey; role?: string | undefined }): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const base = objects[object];
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  // The tab shows the record's name once it has loaded.
  useDocumentTitle(
    load.state === 'ready' ? metadataFor(object, load.record).titleOf?.(load.record) ?? String(load.record.name ?? '') : base.singular,
  );

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const ready = load.state === 'ready' ? load : null;
  // The palette's Recent group lists the records you open.
  const loaded = load.state === 'ready';
  useEffect(() => {
    if (loaded) void recordView(authenticatedFetch, object, id);
  }, [loaded, object, id]);
  const metadata = useMemo(() => {
    if (!ready) return null;
    const laidOut = withSchemaDetails(object, metadataFor(object, ready.record), ready.schema);
    const withPickers = withEditors(laidOut, ready.writable, loadLookupOptions);
    // An opportunity's Primary quote is chosen from its own quotes.
    const edited = object === 'opportunities' && withPickers.details
      ? {
          ...withPickers,
          details: withPickers.details.map((f) =>
            f.key === 'primary_quote_name' && f.editor ? { ...f, editor: { ...f.editor, loadOptions: quoteOptionsFor(authenticatedFetch, id) } } : f,
          ),
        }
      : withPickers;
    if (!edited.related) return edited;
    return {
      ...edited,
      related: edited.related.map((list) => {
        // A join list's role and primary edit in place.
        const writable = ready.joinWritable[list.key];
        const columns = writable ? joinColumns(list.columns, writable, loadLookupOptions) : list.columns;
        // A list the server sorts offers the columns it can sort by.
        return { ...list, columns: ready.paged[list.key] ? serverSortable(columns, ready.related[list.key]?.[0] as ObjectRow | undefined) : columns };
      }),
    };
  }, [object, id, ready]);

  // A quote's workspace tabs (Scope … Proposal) follow Details. The library's
  // tab chooser hides any of them, Details too; this browser remembers which.
  const isQuote = object === 'quotes';
  const quoteTabKeys = useMemo(() => ['details', 'related', ...QUOTE_TAB_KEYS], []);
  const [hiddenTabs, setHiddenTabs] = useState(() => loadHiddenTabs(browserStorage(), quoteTabKeys));
  const workspace = useQuoteWorkspace(id, isQuote, {
    // Repricing changes the totals Details shows.
    onPriced: (totals) =>
      setLoad((current) =>
        current.state === 'ready'
          ? {
              ...current,
              record: {
                ...current.record,
                one_time_list: totals.oneTime.list,
                one_time_net: totals.oneTime.net,
                monthly_list: totals.monthly.list,
                monthly_net: totals.monthly.net,
              },
            }
          : current,
      ),
  });
  const chooseQuoteTabs = (hidden: string[]) => {
    setHiddenTabs(hidden);
    saveHiddenTabs(browserStorage(), hidden);
  };

  // An inline edit: save the one field, then show the record as the server now has it.
  const saveField = useCallback(
    async (key: string, value: string | null) => {
      if (!ready || !metadata) return;
      const saved = await updateRecord(authenticatedFetch, object, id, patchBody(metadata, ready.writable, key, value));
      setLoad((current) => (current.state === 'ready' ? { ...current, record: saved } : current));
      setEdits((n) => n + 1);
      // The workspace shows the quote's name, type and options too.
      if (isQuote) workspace.refresh();
    },
    [ready, metadata, object, id, isQuote, workspace.refresh],
  );

  // Record-level editing: every changed field in one PATCH, then the record as the server has it.
  const saveFields = useCallback(
    async (changes: Record<string, string | null>) => {
      if (!ready || !metadata) return;
      const body = Object.assign({}, ...Object.entries(changes).map(([key, value]) => patchBody(metadata, ready.writable, key, value)));
      const saved = await updateRecord(authenticatedFetch, object, id, body);
      setLoad((current) => (current.state === 'ready' ? { ...current, record: saved } : current));
      setEdits((n) => n + 1);
      if (isQuote) workspace.refresh();
    },
    [ready, metadata, object, id, isQuote, workspace.refresh],
  );

  async function remove() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteRecord(authenticatedFetch, object, id);
      navigate(listPath(object));
    } catch (error) {
      setDeleteError(messageOf(error, `The ${base.singular.toLowerCase()} could not be deleted.`));
      setDeleting(false);
    }
  }

  // A step clicked on the record's path (an opportunity's stage, a lead's status), waiting to be confirmed.
  const pathDef = PATHS[object];
  const [pendingStage, setPendingStage] = useState<string | null>(null);
  const [closedOutcome, setClosedOutcome] = useState('');
  const [stageBusy, setStageBusy] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);

  async function changeStage() {
    if (!pendingStage || !pathDef) return;
    setStageBusy(true);
    setStageError(null);
    try {
      const to = pendingStage === CLOSED ? closedOutcome : pendingStage;
      await saveField(pathDef.field, to);
      setPendingStage(null);
      strategy.afterStageChange(to);
    } catch (error) {
      setStageError(messageOf(error, `The ${pathDef.noun} could not be updated.`));
    } finally {
      setStageBusy(false);
    }
  }

  // Closing an opportunity needs a Deal Retro (0053): the Closed step adds one first, then asks again.
  const [resumeClose, setResumeClose] = useState<string | null>(null);
  const needsDealRetro =
    object === 'opportunities' && pendingStage === CLOSED && ready !== null && ready.record.closed !== true && ready.totals['deal-retros'] === 0;
  useEffect(() => {
    if (resumeClose === null || load.state !== 'ready' || !(load.totals['deal-retros'] ?? 0)) return;
    setClosedOutcome(resumeClose);
    setStageError(null);
    setPendingStage(CLOSED);
    setResumeClose(null);
  }, [resumeClose, load]);

  const [task, setTask] = useState<RelatedTask | null>(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const reload = () => setAttempt((n) => n + 1);
  // A message about the page as a whole: a list that could not open its editor, a product that could not activate.
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  // A notice the page was opened with ("Merged into …" after a merge) shows once: a reload does not repeat it.
  const location = useLocation();
  useEffect(() => {
    const carried = (location.state as { notice?: unknown } | null)?.notice;
    if (typeof carried !== 'string' || carried === '') return;
    setNotice({ tone: 'success', text: carried });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, navigate]);
  // A contact or an account merges with another (recordMerge.tsx); a merge deletes one, so it needs delete.
  const mergeSpec = mergeSpecOf(object);
  const canMerge = mergeSpec !== null && Boolean(ready?.writable.delete);
  const [mergeOpen, setMergeOpen] = useState(false);
  // The search the object's other lookup fields use.
  const searchToMerge = useMemo(() => loadLookupOptions(object), [object]);

  // An opportunity's strategy and pre-mortem (opportunities/StrategyTab.tsx): the header's Strategy action and its tab.
  const isOpportunity = object === 'opportunities';
  const strategy = useOpportunityStrategy(isOpportunity ? id : null);
  // A guideline the playbook proposed: inactive until a person accepts or dismisses it.
  const [reviewBusy, setReviewBusy] = useState(false);
  async function decideGuideline(decision: 'accept' | 'dismiss') {
    setReviewBusy(true);
    setNotice(null);
    try {
      await reviewGuideline(id, decision);
      setNotice({ tone: 'success', text: decision === 'accept' ? 'Accepted: the strategy agent reads it from now on.' : 'Dismissed: it stays out of the playbook.' });
      reload();
    } catch (error) {
      setNotice({ tone: 'error', text: messageOf(error, 'That could not be saved.') });
    } finally {
      setReviewBusy(false);
    }
  }
  async function runStrategyFromHeader() {
    setNotice(null);
    const run = await strategy.run('strategy');
    if (run?.state === 'done') setNotice({ tone: 'success', text: 'The strategy is ready on the Strategy tab.' });
    else if (run?.state === 'error') setNotice({ tone: 'error', text: `The strategy could not run: ${run.error ?? 'unknown error'}` });
    else if (run) setNotice({ tone: 'success', text: 'The strategy is queued for the AI service. It shows on the Strategy tab when it is written.' });
  }

  // A product's setup checklist (ops.product_readiness), read again after every change to the product.
  const isProduct = object === 'products';
  const [editingSchedules, setEditingSchedules] = useState(false);
  const [scheduleEdits, setScheduleEdits] = useState(0);
  const [edits, setEdits] = useState(0);
  const product = useProductReadiness(id, isProduct, attempt + edits);
  // The product page's tabs and summary cards (products/ProductPage.tsx), read again after every edit.
  const productPage = useProductPage(id, isProduct, attempt + edits);
  // Who changed the record's tracked fields, read again whenever the record changes (MOTHER-83).
  const history = useRecordHistory(object, id, load.state === 'ready' ? load.record : null, authenticatedFetch);
  const mergeHistory = useMergeHistory(authenticatedFetch, object, id, role);
  // An opportunity's summary cards and rail, read again after every edit and schedule change.
  const [summary, setSummary] = useState<Summary | null>(null);
  useEffect(() => {
    if (!isOpportunity) return;
    let live = true;
    loadSummary(id)
      .then((s) => { if (live) setSummary(s); })
      .catch(() => { if (live) setSummary(null); });
    return () => { live = false; };
  }, [isOpportunity, id, attempt, edits, scheduleEdits]);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  async function changeStatus(to: 'active' | 'retired') {
    setStatusBusy(true);
    setNotice(null);
    try {
      if (to === 'active') await activateProduct(authenticatedFetch, id);
      else await retireProduct(authenticatedFetch, id);
      setConfirmRetire(false);
      setNotice({ tone: 'success', text: to === 'active' ? 'Activated: it is offered on new quotes now.' : 'Retired: it is no longer offered on new quotes.' });
      reload();
    } catch (error) {
      setConfirmRetire(false);
      setNotice({ tone: 'error', text: messageOf(error, 'The product could not be updated.') });
      if (error instanceof ProductSetupError && error.readiness) setEdits((n) => n + 1);
    } finally {
      setStatusBusy(false);
    }
  }

  // Each paged list's header sort and quick filter, and its latest request, so an older answer is dropped.
  const relatedQueries = useRef<Record<string, RelatedListQuery>>({});
  const relatedRequests = useRef<Record<string, number>>({});
  useEffect(() => { relatedQueries.current = {}; }, [object, id]);

  // One related list again, as the server now has it, without reloading the page.
  const refreshRelated = useCallback(async (key: string) => {
    const list = metadata?.related?.find((l) => l.key === key);
    if (!list) return;
    const request = (relatedRequests.current[key] ?? 0) + 1;
    relatedRequests.current[key] = request;
    try {
      const page = await parseObjectListResponse(
        await authenticatedFetch(relatedUrl(object, id, key, { limit: list.previewCount ?? 5, offset: 0, ...queryParams(relatedQueries.current[key]) })),
      );
      if (relatedRequests.current[key] !== request) return;
      setLoad((current) =>
        current.state === 'ready'
          ? { ...current, related: { ...current.related, [key]: page.rows }, totals: { ...current.totals, [key]: page.total } }
          : current,
      );
    } catch {
      // The list keeps what it showed until the page reloads.
    }
  }, [metadata, object, id]);

  // A meeting's AI-suggested links (MOTHER-204): loaded once with the page and again after each
  // decision, with no polling. A failed load shows nothing: the rest of the page is unaffected.
  const [suggestions, setSuggestions] = useState<SuggestedLink[]>([]);
  const [suggestionsBusy, setSuggestionsBusy] = useState<string[]>([]);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [suggestionsLoads, setSuggestionsLoads] = useState(0);
  // Decided on this page: a load that started before the decision committed does not bring one back.
  const decided = useRef(new Set<string>());
  const suggestionsFor = useRef(id);
  useEffect(() => {
    suggestionsFor.current = id;
    if (object !== 'meetings') return undefined;
    let live = true;
    loadLinkSuggestions(id)
      .then((answer) => { if (live) setSuggestions(answer.suggestions.map(toSuggestedLink).filter((s) => !decided.current.has(s.id))); })
      .catch(() => { if (live) setSuggestions([]); });
    return () => { live = false; };
  }, [object, id, suggestionsLoads]);
  useEffect(() => {
    decided.current = new Set();
    setSuggestions([]);
    setSuggestionsBusy([]);
    setSuggestionsError(null);
  }, [object, id]);

  // Accept links the record and reads the related lists again; Dismiss only drops the row. A 409 (decided
  // already, here or elsewhere) drops the row and reads everything again; any other refusal keeps the row.
  const decideSuggestion = useCallback((suggestion: SuggestedLink, decision: LinkDecision) => {
    const meetingId = id;
    setSuggestionsError(null);
    setSuggestionsBusy((busy) => [...busy, suggestion.id]);
    void decideLinkSuggestion(meetingId, suggestion.id, decision)
      .then(() => ({ done: true, conflict: false }))
      .catch((error: unknown) => {
        if (error instanceof LinkSuggestionError && error.status === 409) return { done: true, conflict: true };
        if (suggestionsFor.current === meetingId) setSuggestionsError(messageOf(error, 'The suggestion could not be saved.'));
        return { done: false, conflict: false };
      })
      .then(({ done, conflict }) => {
        if (suggestionsFor.current !== meetingId) return;
        setSuggestionsBusy((busy) => busy.filter((s) => s !== suggestion.id));
        if (!done) return;
        decided.current.add(suggestion.id);
        setSuggestions((list) => list.filter((s) => s.id !== suggestion.id));
        if (decision === 'accept' || conflict) for (const key of MEETING_LINK_LISTS) void refreshRelated(key);
        setSuggestionsLoads((n) => n + 1);
      });
  }, [id, refreshRelated]);

  // A related list's New/Add and its row menu (Delete or Remove), from what /meta says it can do.
  const editActions = useCallback(
    (relationKey: string): RelatedListActions | undefined => {
      if (!ready || !metadata) return undefined;
      // An opportunity's schedule rows edit in place, through the schedule editor's rules.
      // Edit schedules opens the grid from the list's header.
      if (object === 'opportunities' && relationKey === 'revenue') {
        return {
          newLabel: 'Edit schedules',
          onNew: () => setEditingSchedules(true),
          onCellSave: async (row: ObjectRow, key: string, value: string | null) => {
            await patchScheduleRow(id, String(row.id), key, value);
            setScheduleEdits((n) => n + 1);
            void refreshRelated('revenue');
          },
        };
      }
      const relation = ready.relations[relationKey];
      const list = metadata.related?.find((r) => r.key === relationKey);
      if (!relation || !list) return undefined;
      if (relation.kind === 'child') {
        const child = objectKeyOf(relation.object);
        if (!isObjectKey(child)) return undefined;
        const childMeta = objects[child];
        const joinWritable = ready.joinWritable[relationKey];
        // A join list adds the record on its other side: "Add contact".
        const added = joinWritable?.fields.find((w) => w.type === 'lookup' && w.key !== relation.foreignKey)?.lookup?.object;
        const addedKey = added ? objectKeyOf(added) : null;
        return {
          newLabel: relation.join && addedKey && isObjectKey(addedKey)
            ? `Add ${objects[addedKey].singular.toLowerCase()}`
            : `New ${childMeta.singular.toLowerCase()}`,
          ...(joinWritable
            ? {
                // A cell edited in place; the list is read again, since making one primary unticks another.
                onCellSave: async (row: ObjectRow, key: string, value: string | null) => {
                  const columns = metadata.related?.find((r) => r.key === relationKey)?.columns ?? [];
                  await updateRecord(authenticatedFetch, child, row.id, cellBody(columns, joinWritable, key, value));
                  await refreshRelated(relationKey);
                },
              }
            : {}),
          onNew: () => {
            void (async () => {
              const meta = await parseObjectMetaResponse(await authenticatedFetch(objectMetaUrl(child)));
              const writable = meta.writable ?? NOT_WRITABLE;
              if (!writable.create) return;
              const inherit = relation.inherit ?? {};
              const inherited = Object.fromEntries(
                Object.entries(inherit).map(([field, parentField]) => [field, ready.record[parentField] ?? null]),
              );
              if (relation.join) {
                // A join list asks only for the other side: the lookup back here and what it inherits stay hidden.
                // An opportunity's contact picker starts with its account's contacts.
                const accountId = typeof ready.record.account_id === 'string' ? ready.record.account_id : null;
                const contactPicker = object === 'opportunities' && relationKey === 'contact-roles'
                  ? contactOptionsFor(authenticatedFetch, accountId)
                  : null;
                setTaskError(null);
                setTask({
                  step: 'form',
                  title: `Add ${childMeta.singular.toLowerCase()}`,
                  fields: formFields(childMeta, writable, loadLookupOptions)
                    .filter((f) => f.key !== relation.foreignKey && !Object.hasOwn(inherited, f.key))
                    .map((f) => (contactPicker && f.key === 'contact_id' && f.editor ? { ...f, editor: { ...f.editor, loadOptions: contactPicker } } : f)),
                  submit: async (values) => {
                    await createRecord(authenticatedFetch, child, {
                      ...createBody(writable, values),
                      ...inherited,
                      [relation.foreignKey]: id,
                    });
                    setTask(null);
                    reload();
                  },
                });
                return;
              }
              // The child's own New form, as its list opens it, with the lookup back
              // to this record and what it inherits from it filled in.
              const fields = formFields(childMeta, writable, loadLookupOptions);
              const defaults = parentDefaults(base, ready.record, relation.foreignKey, inherit);
              const shown = new Set(fields.map((f) => f.key));
              setTaskError(null);
              setTask({
                step: 'form',
                title: `New ${childMeta.singular.toLowerCase()}`,
                fields,
                initialValues: defaults.values,
                initialLabels: defaults.labels,
                submit: async (values) => {
                  // A default the form does not show is still sent.
                  const hidden = Object.fromEntries(Object.entries(defaults.values).filter(([key]) => !shown.has(key)));
                  await createRecord(authenticatedFetch, child, { ...hidden, ...createBody(writable, values) });
                  setTask(null);
                  // Only this list is read again, so the page stays on its tab.
                  await refreshRelated(relationKey);
                  setEdits((n) => n + 1);
                },
              });
            })();
          },
          rowActions: [{
            label: 'Edit',
            onSelect: (row: ObjectRow) => {
              void (async () => {
                // The row's own writable fields as the child's record has them, less the
                // lookup back to this record, what it inherits, and other lookups (edited on its page).
                const [meta, child_] = await Promise.all([
                  authenticatedFetch(objectMetaUrl(child)).then(parseObjectMetaResponse),
                  authenticatedFetch(recordUrl(child, row.id)).then(parseRecordResponse),
                ]);
                const writable = meta.writable ?? NOT_WRITABLE;
                const editable = writable.fields.filter(
                  (w) => w.key !== relation.foreignKey && !Object.hasOwn(relation.inherit ?? {}, w.key) && w.type !== 'lookup',
                );
                if (editable.length === 0) {
                  navigate(recordPath(child, row.id));
                  return;
                }
                const fields = formFields(childMeta, { ...writable, fields: editable }, loadLookupOptions);
                const initialValues = Object.fromEntries(
                  editable.flatMap((w) => {
                    const value = child_[w.key];
                    return value === null || value === undefined ? [] : [[w.key, String(value)]];
                  }),
                );
                setTaskError(null);
                setTask({
                  step: 'form',
                  title: `Edit ${childMeta.singular.toLowerCase()}`,
                  fields,
                  initialValues,
                  submitLabel: 'Save',
                  submit: async (values) => {
                    const body: Record<string, unknown> = {};
                    for (const w of editable) {
                      const next = values[w.key] ?? null;
                      if ((next ?? '') !== (initialValues[w.key] ?? '')) body[w.key] = toWire(w, next);
                    }
                    if (Object.keys(body).length > 0) await updateRecord(authenticatedFetch, child, row.id, body);
                    setTask(null);
                    reload();
                  },
                });
              })().catch((error: unknown) => setNotice({ tone: 'error', text: messageOf(error, 'That row could not be opened for editing.') }));
            },
          }, {
            label: relation.join ? 'Remove' : 'Delete',
            destructive: true,
            onSelect: (row: ObjectRow) => {
              setTaskError(null);
              setTask({
                step: 'confirm',
                verb: relation.join ? 'Remove' : 'Delete',
                title: relation.join ? `Remove ${String(row.name ?? '')} from this ${base.singular.toLowerCase()}?` : `Delete this ${childMeta.singular.toLowerCase()}?`,
                name: relation.join ? String(row.name ?? '') : childMeta.titleOf?.(row) ?? String(row.name ?? ''),
                run: async () => { await deleteRecord(authenticatedFetch, child, row.id); setTask(null); reload(); },
              });
            },
          }],
        };
      }
      const target = relation.object ? objectKeyOf(relation.object) : list.object;
      if (!target || !isObjectKey(target)) return undefined;
      const noun = singularOf(list.label);
      return {
        newLabel: `Add ${noun}`,
        onNew: () => {
          setTaskError(null);
          setTask({
            step: 'form',
            title: `Add ${noun}`,
            fields: [{ key: 'target', label: objects[target].singular, required: true, editor: { kind: 'lookup', loadOptions: loadLookupOptions(target) } }],
            submit: async (values) => {
              await addLink(authenticatedFetch, object, id, relationKey, values.target ?? '');
              setTask(null);
              reload();
            },
          });
        },
        rowActions: [{
          label: 'Remove',
          destructive: true,
          onSelect: (row: ObjectRow) => {
            setTaskError(null);
            setTask({
              step: 'confirm',
              verb: 'Remove',
              title: `Remove this ${noun}?`,
              name: String(row.name ?? ''),
              run: async () => { await removeLink(authenticatedFetch, object, id, relationKey, row.id); setTask(null); reload(); },
            });
          },
        }],
      };
    },
    [ready, metadata, object, id, navigate, base.singular, refreshRelated],
  );

  // A list with more rows than it loaded sorts and filters on the server; the rest do so in place.
  const relatedActions = useCallback(
    (relationKey: string): RelatedListActions | undefined => {
      const actions = editActions(relationKey);
      if (!ready?.paged[relationKey]) return actions;
      const change = (next: Partial<RelatedListQuery>) => {
        relatedQueries.current[relationKey] = { search: '', ...relatedQueries.current[relationKey], ...next };
        void refreshRelated(relationKey);
      };
      return {
        ...actions,
        mode: 'server',
        onSortChange: (sort) => change({ sort }),
        onSearchChange: (search) => change({ search }),
      };
    },
    [editActions, ready, refreshRelated],
  );

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoad({ state: 'loading' });
    (async () => {
      // What may be edited; without it the page is read-only rather than broken.
      const metaPromise = authenticatedFetch(objectMetaUrl(object), { signal })
        .then(parseObjectMetaResponse)
        .then((meta) => ({
          writable: meta.writable ?? NOT_WRITABLE,
          relations: meta.relations ?? {},
          schema: { columns: meta.columns ?? [], fields: meta.fields },
        }))
        .catch(() => ({ writable: NOT_WRITABLE, relations: {}, schema: null }));
      const record = await parseRecordResponse(await authenticatedFetch(recordUrl(object, id), { signal }));
      const lists = metadataFor(object, record).related ?? [];
      // Each list shows its first rows; one that fails to load shows empty, with a notice.
      const results = await Promise.allSettled(
        lists.map(async (list) => {
          // A reload keeps each list's sort and quick filter.
          const url = relatedUrl(object, id, list.key, { limit: list.previewCount ?? 5, offset: 0, ...queryParams(relatedQueries.current[list.key]) });
          return [list.key, await parseObjectListResponse(await authenticatedFetch(url, { signal }))] as const;
        }),
      );
      if (signal.aborted) return;
      const related: Record<string, unknown[]> = {};
      const totals: Record<string, number> = {};
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        related[result.value[0]] = result.value[1].rows;
        totals[result.value[0]] = result.value[1].total;
      }
      const { writable, relations, schema } = await metaPromise;
      // A join list edits in place only when its object says what may be written.
      const joins = await Promise.all(
        Object.entries(relations).flatMap(([key, relation]) => {
          if (relation.kind !== 'child' || !relation.join) return [];
          const child = objectKeyOf(relation.object);
          if (!isObjectKey(child)) return [];
          return [
            authenticatedFetch(objectMetaUrl(child), { signal })
              .then(parseObjectMetaResponse)
              .then((meta) => [key, meta.writable ?? NOT_WRITABLE] as const)
              .catch(() => [key, NOT_WRITABLE] as const),
          ];
        }),
      );
      const joinWritable = Object.fromEntries(joins.filter(([, w]) => w.fields.length > 0));
      if (signal.aborted) return;
      setLoad({
        state: 'ready', record, related, totals,
        paged: Object.fromEntries(
          Object.entries(totals).map(([key, total]) => [key, total > (related[key]?.length ?? 0) || Object.hasOwn(relatedQueries.current, key)]),
        ),
        relatedFailed: results.some((r) => r.status === 'rejected'), writable, relations, joinWritable, schema,
      });
    })().catch((error: unknown) => {
      if (signal.aborted) return;
      if (error instanceof ObjectsApiError && error.status === 404) setLoad({ state: 'missing' });
      else setLoad({ state: 'error', message: messageOf(error, `The ${base.singular.toLowerCase()} could not be loaded.`) });
    });
    return () => controller.abort();
  }, [object, id, attempt, base.singular]);

  // Clone opens a new record started from this one; a product is set up by its own flow instead.
  const canClone = Boolean(ready?.writable.create) && clonable(object);

  // ⌘K on a record page: this record's actions first (edit a field, add to a
  // related list, copy its link, delete it).
  const paletteActions = useMemo<CommandItem[]>(() => {
    if (!ready || !metadata) return [];
    const group = `This ${base.singular.toLowerCase()}`;
    const editable = (metadata.details ?? []).filter((f) => f.editor && typeof f.label === 'string');
    const related = (metadata.related ?? []).flatMap((list) => {
      const actions = relatedActions(list.key);
      if (!actions?.onNew) return [];
      // "New contact on this account" sits apart from the palette's plain "New contact".
      const relation = ready.relations[list.key];
      const child = relation?.kind === 'child' ? objectKeyOf(relation.object) : null;
      const noun = child && isObjectKey(child) ? objects[child].singular.toLowerCase() : singularOf(list.label);
      const label =
        !actions.newLabel || actions.newLabel === 'New' || actions.newLabel === `New ${noun}`
          ? `New ${noun} on this ${base.singular.toLowerCase()}`
          : `${actions.newLabel} to this ${base.singular.toLowerCase()}`;
      return [{ id: `record:new:${list.key}`, label, group, icon: base.icon, keywords: `add create ${list.label}`, onSelect: actions.onNew }];
    });
    return [
      ...editable.map((field) => ({
        id: `record:edit:${field.key}`,
        label: `Edit ${String(field.label)}`,
        group,
        icon: base.icon,
        keywords: 'change update field',
        // The field's own pencil: the same inline editor a click would open.
        onSelect: () => {
          const button = [...document.querySelectorAll<HTMLButtonElement>('main button[aria-label]')].find(
            (b) => b.getAttribute('aria-label') === `Edit ${String(field.label)}`,
          );
          button?.scrollIntoView({ block: 'center' });
          button?.click();
        },
      })),
      ...related,
      ...(canClone
        ? [{
            id: 'record:clone',
            label: `Clone this ${base.singular.toLowerCase()}`,
            group,
            icon: base.icon,
            keywords: 'copy duplicate new',
            onSelect: () => navigate(clonePath(object, id)),
          }]
        : []),
      ...(canMerge && mergeSpec
        ? [{
            id: 'record:merge',
            label: mergeSpec.withAnotherLabel,
            group,
            icon: base.icon,
            keywords: 'duplicate combine dedupe',
            onSelect: () => { setNotice(null); setMergeOpen(true); },
          }]
        : []),
      {
        id: 'record:copy-link',
        label: 'Copy link',
        group,
        icon: base.icon,
        keywords: 'url share',
        onSelect: () => { void navigator.clipboard?.writeText(window.location.href); },
      },
      ...(ready.writable.delete
        ? [{
            id: 'record:delete',
            label: `Delete this ${base.singular.toLowerCase()}`,
            group,
            icon: base.icon,
            keywords: 'remove',
            onSelect: () => { setDeleteError(null); setConfirmDelete(true); },
          }]
        : []),
    ];
  }, [ready, metadata, relatedActions, base, canClone, canMerge, mergeSpec, navigate, object, id]);
  usePageActions(paletteActions);

  if (load.state === 'loading') {
    return (
      <PageContent as="section" width="wide">
        <Spinner label={`Loading ${base.singular.toLowerCase()}…`} />
      </PageContent>
    );
  }
  if (load.state === 'missing') {
    return (
      <PageContent as="section" width="wide">
        <EmptyState
          icon={base.icon}
          title={`${base.singular} not found`}
          description="It may have been deleted, or the link is wrong."
          action={
            <Button asChild variant="neutral">
              <Link to={listPath(object)}>All {base.plural.toLowerCase()}</Link>
            </Button>
          }
        />
      </PageContent>
    );
  }
  if (load.state === 'error') {
    return (
      <PageContent as="section" width="wide">
        <ErrorState title={`Could not load this ${base.singular.toLowerCase()}`} description={load.message} onRetry={() => setAttempt((n) => n + 1)} />
      </PageContent>
    );
  }

  if (!metadata) return <></>;
  const title = metadata.titleOf?.(load.record) ?? String(load.record.name ?? '');
  const isProposedGuideline = object === 'guidelines' && load.record.review_status === 'proposed';
  // Below the details: the object's own section (a price grid's cells, a product's checklist, fields
  // from history), then the records merged into it (MOTHER-180) and the History section, each once
  // it has something to show (MOTHER-83).
  const ownFooter = object === 'price-grids' ? (
    // Its cells, edited as one grid, above what history and AI columns show.
    <Stack>
      <PriceGridCellsEditor gridId={id} onSaved={reload} />
      {isDerivable(object) ? <FromHistorySection object={object} recordId={id} onNavigate={(href) => navigate(href)} /> : null}
    </Stack>
  ) : isProduct ? (
    <ProductSetupChecklist readiness={product.readiness} error={product.error} status={String(load.record.status ?? '')} />
  ) : isDerivable(object) ? (
    <FromHistorySection object={object} recordId={id} onNavigate={(href) => navigate(href)} />
  ) : null;
  // A research source's PDF opens from the header, beside its other actions.
  const sourcePdf = object === 'research-sources' && typeof load.record.document_id === 'string' && load.record.document_id !== ''
    ? { id: load.record.document_id, name: String(load.record.document_name ?? '') }
    : null;
  const historySection = historyShown(history.history) ? <RecordHistorySection history={history.history} onShowMore={history.showMore} /> : null;
  const mergedSection = mergeHistory ? <MergedRecordsSection history={mergeHistory} singular={base.singular} /> : null;
  // Accept and Dismiss for whoever can add a meeting's links by hand (the related lists' Add).
  const suggestedSection = object === 'meetings' && (suggestions.length > 0 || suggestionsError) ? (
    <SuggestedLinksSection
      suggestions={suggestions}
      onAccept={(s) => decideSuggestion(s, 'accept')}
      onDismiss={(s) => decideSuggestion(s, 'dismiss')}
      busyIds={suggestionsBusy}
      error={suggestionsError}
      canDecide={MEETING_LINK_LISTS.some((key) => load.relations[key]?.kind === 'link')}
    />
  ) : null;
  const footerParts = [ownFooter, suggestedSection, mergedSection, historySection].filter((part) => part !== null);
  const detailsFooter = footerParts.length > 1
    ? <Stack>{ownFooter}{suggestedSection}{mergedSection}{historySection}</Stack>
    : (footerParts[0] ?? null);
  // One of the record's own related lists inside a custom tab, with the same actions as on Related.
  const relatedListFor = (key: string) => {
    const list = (metadata.related ?? []).find((l) => l.key === key);
    if (!list) return null;
    const actions = relatedActions(key);
    const href = relatedPath(object, id, key);
    return (
      <RelatedList<ObjectRow>
        key={key}
        title={list.label}
        columns={list.columns}
        rows={(load.related[key] ?? []) as ObjectRow[]}
        getRowId={(r) => String(r.id)}
        previewCount={50}
        {...(load.totals[key] !== undefined ? { total: load.totals[key] } : {})}
        viewAllHref={href}
        {...(list.object ? { object: list.object } : {})}
        recordHref={recordHref}
        onNavigate={(to) => navigate(to)}
        {...(actions ?? {})}
      />
    );
  };
  const productTab = (content: (data: NonNullable<typeof productPage.data>) => JSX.Element) =>
    productPage.data ? content(productPage.data) : <PagePending error={productPage.error} onRetry={productPage.reload} />;
  const productChanged = () => { productPage.reload(); setEdits((n) => n + 1); };
  const productError = (text: string) => setNotice({ tone: 'error', text });
  return (
    <PageContent as="section" width="wide">
      {load.relatedFailed ? (
        <InlineMessage tone="warning" role="status" className="mb-3">
          Some related lists could not be loaded and show as empty. Refresh to try again.
        </InlineMessage>
      ) : null}
      {notice ? (
        <InlineMessage tone={notice.tone} role={notice.tone === 'error' ? 'alert' : 'status'} className="mb-3">
          {notice.text}
        </InlineMessage>
      ) : null}
      <ObjectRecordPage<ObjectRow>
        key={`${object}:${id}`}
        metadata={metadata}
        record={load.record}
        related={load.related}
        recordHref={recordHref}
        onNavigate={(href) => navigate(href)}
        {...(load.writable.fields.length ? { onFieldsSave: saveFields } : {})}
        {...(isQuote ? { extraTabs: workspace.tabs, hiddenTabs, onHiddenTabsChange: chooseQuoteTabs } : {})}
        {...(object === 'meetings'
          ? {
              extraTabs: [{
                key: 'transcript',
                label: 'Transcript',
                after: 'summary',
                content: (
                  <TranscriptTab
                    meetingId={id}
                    title={String(load.record.name ?? '')}
                    date={typeof load.record.meeting_date === 'string' ? load.record.meeting_date : null}
                  />
                ),
              }],
            }
          : {})}
        {...(object === 'activities' && load.record.kind === 'follow_up'
          // A follow-up opens on its plan: the step, the message, why, and the research behind it.
          ? { summary: <FollowUpPlan key={edits} id={id} /> }
          : {})}
        {...(object === 'contacts' || object === 'accounts' || object === 'leads' || object === 'projects'
          ? {
              // The timeline, right after Information (a project's, after Delivery: MOTHER-243). An account's
              // and a project's related-list tab of meetings, activities and documents is History (accounts.tsx,
              // projects.tsx), so this is their only Activity tab. A lead's related lists stay on its
              // Conversations tab (leads.tsx).
              extraTabs: [{
                key: 'activity',
                label: 'Activity',
                after: object === 'projects' ? 'delivery' : 'information',
                content: <ActivityTab key={edits} object={object} recordId={id} onNavigate={(href) => navigate(href)} />,
              }],
            }
          : {})}
        {...(isOpportunity
          ? {
              ...(summary
                ? {
                    summary: (
                      <OpportunitySummary
                        summary={summary}
                        onSaveCloseDate={load.writable.fields.some((f) => f.key === 'close_date') ? (value) => saveField('close_date', value) : undefined}
                        onSaveProbability={load.writable.fields.some((f) => f.key === 'probability') ? (value) => saveField('probability', value) : undefined}
                        onEditSchedule={() => setEditingSchedules(true)}
                      />
                    ),
                    rail: (
                      <Stack gap="md">
                        <NextStepCard
                          value={summary.opportunity.next_step}
                          onSave={load.writable.fields.some((f) => f.key === 'next_step') ? (value) => saveField('next_step', value) : undefined}
                        />
                        <QuoteLinesCard summary={summary} onNavigate={(href) => navigate(href)} onRegenerated={() => setScheduleEdits((n) => n + 1)} />
                      </Stack>
                    ),
                  }
                : {}),
              relatedPlacement: 'tab' as const,
              relatedTabAfter: 'activity',
              extraTabs: [
                {
                  key: 'revenue',
                  label: 'Revenue',
                  content: (
                    <RevenuePlanTab
                      key={scheduleEdits}
                      opportunityId={id}
                      onEditSchedules={() => setEditingSchedules(true)}
                      onNavigate={(href) => navigate(href)}
                    />
                  ),
                },
                { key: 'contracting', label: 'Contracting', content: <ContractingTab opportunityId={id} onNavigate={(href) => navigate(href)} /> },
                { key: 'delivery', label: 'Delivery', content: <DeliveryTab key={scheduleEdits} opportunityId={id} /> },
                { key: 'activity', label: 'Activity', content: <ActivityTab key={edits} object="opportunities" recordId={id} onNavigate={(href) => navigate(href)} /> },
                { key: 'strategy', label: 'Strategy', content: <StrategyTab strategy={strategy} closed={load.record.closed === true} /> },
              ],
            }
          : {})}
        {...(load.writable.delete || isProduct || canClone || isOpportunity || isProposedGuideline || sourcePdf
          ? {
              actions: (
                <>
                  {sourcePdf ? <OpenDocumentButton id={sourcePdf.id} name={sourcePdf.name} /> : null}
                  {isProposedGuideline ? (
                    <>
                      <Button variant="brand" loading={reviewBusy} disabled={reviewBusy} onClick={() => void decideGuideline('accept')}>Accept</Button>
                      <Button variant="neutral" disabled={reviewBusy} onClick={() => void decideGuideline('dismiss')}>Dismiss</Button>
                    </>
                  ) : null}
                  {isOpportunity ? (
                    <Button
                      variant="neutral"
                      loading={strategy.busy === 'strategy'}
                      disabled={strategy.busy !== null}
                      onClick={() => void runStrategyFromHeader()}
                    >
                      <AiMark /> Strategy
                    </Button>
                  ) : null}
                  {canClone ? (
                    <Button variant="neutral" onClick={() => navigate(clonePath(object, id))}>
                      Clone
                    </Button>
                  ) : null}
                  {/* A product activates once nothing blocks it (the database's gate refuses it otherwise), and retires when no longer sold. */}
                  {isProduct && load.record.status !== 'active' ? (
                    <Button variant="brand" disabled={!product.readiness?.ready || statusBusy} loading={statusBusy} onClick={() => void changeStatus('active')}>
                      Activate
                    </Button>
                  ) : null}
                  {isProduct && load.record.status === 'active' ? (
                    <Button variant="neutral" disabled={statusBusy} onClick={() => setConfirmRetire(true)}>
                      Retire
                    </Button>
                  ) : null}
                  {canMerge && mergeSpec ? (
                    <Button variant="neutral" onClick={() => { setNotice(null); setMergeOpen(true); }}>
                      {mergeSpec.withAnotherLabel}
                    </Button>
                  ) : null}
                  {load.writable.delete ? (
                    <Button variant="neutral" onClick={() => { setDeleteError(null); setConfirmDelete(true); }}>
                      Delete
                    </Button>
                  ) : null}
                </>
              ),
            }
          : {})}
        {...(pathDef
          ? {
              path: {
                ...pathOf(pathDef, load.record),
                ...(load.writable.fields.some((f) => f.key === pathDef.field)
                  ? {
                      onStageClick: (key: string) => {
                        // Clicking the current step does nothing; Closed can switch between its two outcomes.
                        if (key !== CLOSED && key === load.record[pathDef.field]) return;
                        setStageError(null);
                        setClosedOutcome(proposedValue(pathDef, load.record, CLOSED));
                        setPendingStage(key);
                      },
                    }
                  : {}),
              },
            }
          : {})}
        {...(isProduct
          ? {
              ...(productPage.data ? { summary: <ProductSummary data={productPage.data} record={load.record} /> } : {}),
              relatedPlacement: 'tab' as const,
              extraTabs: [
                { key: 'catalog', label: PRODUCT_TABS.catalog, content: productTab((d) => <CatalogTab data={d} onNavigate={(to) => navigate(to)} onChanged={productChanged} onError={productError} />) },
                { key: 'pricing', label: PRODUCT_TABS.pricing, content: <Stack>{relatedListFor('prices')}</Stack> },
                ...(load.record.structure === 'bundle'
                  ? [{ key: 'structure', label: PRODUCT_TABS.structure, content: productTab((d) => <StructureTab data={d} record={load.record} onNavigate={(to) => navigate(to)} onChanged={productChanged} onError={productError} />) }]
                  : []),
                { key: 'billing', label: PRODUCT_TABS.billing, content: productTab((d) => <BillingTab data={d} onNavigate={(to) => navigate(to)} />) },
                { key: 'operations', label: PRODUCT_TABS.operations, content: <Stack>{relatedListFor('role-shares')}</Stack> },
                { key: 'sales', label: PRODUCT_TABS.sales, content: productTab((d) => <SalesTab data={d} onNavigate={(to) => navigate(to)} />) },
              ],
            }
          : {})}
        {...(detailsFooter ? { detailsFooter } : {})}
        relatedTotals={load.totals}
        viewAllHref={(relationKey) => relatedPath(object, id, relationKey)}
        relatedActions={relatedActions}
      />
      {editingSchedules ? (
        <RevenueScheduleEditor
          opportunityId={id}
          onClose={() => setEditingSchedules(false)}
          onSaved={() => {
            setScheduleEdits((n) => n + 1);
            void refreshRelated('revenue');
          }}
        />
      ) : null}
      {canMerge && mergeSpec ? (
        <MergeWithAnother
          spec={mergeSpec}
          fetcher={authenticatedFetch}
          recordId={id}
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          search={searchToMerge}
          onMerged={(survivorId, name) => {
            setMergeOpen(false);
            navigate(recordPath(object, survivorId), { state: { notice: `Merged into ${name}.` } });
            // Kept this record: the same page, read again with what it took from the other.
            if (survivorId === id) reload();
          }}
          onFailed={(text) => setNotice({ tone: 'error', text })}
        />
      ) : null}
      <RecordFormDialog
        open={task?.step === 'form'}
        onOpenChange={(open) => { if (!open) { setTask(null); setResumeClose(null); } }}
        key={task?.step === 'form' ? task.title : 'none'}
        title={task?.step === 'form' ? task.title : ''}
        fields={task?.step === 'form' ? task.fields : []}
        {...(task?.step === 'form' && task.initialValues ? { initialValues: task.initialValues } : {})}
        {...(task?.step === 'form' && task.initialLabels ? { initialLabels: task.initialLabels } : {})}
        {...(task?.step === 'form' && task.submitLabel ? { submitLabel: task.submitLabel } : {})}
        onSubmit={(values) => (task?.step === 'form' ? task.submit(values) : undefined)}
      />
      <ConfirmDialog
        open={task?.step === 'confirm'}
        onOpenChange={(open) => { if (!open && !taskBusy) setTask(null); }}
        title={task?.step === 'confirm' ? task.title : ''}
        description={
          task?.step === 'confirm' ? (
            <>
              <strong>{task.name}</strong>{' '}
              {task.verb === 'Delete' ? `moves to the Recycle Bin. ${restoreNote()}` : 'will be taken out of this list. The record itself is kept.'}
              {taskError ? <InlineMessage tone="error" role="alert" className="mt-3">{taskError}</InlineMessage> : null}
            </>
          ) : null
        }
        confirmLabel={task?.step === 'confirm' ? task.verb : 'Confirm'}
        tone="danger"
        busy={taskBusy}
        onConfirm={() => {
          if (task?.step !== 'confirm') return;
          setTaskBusy(true);
          setTaskError(null);
          task.run()
            .catch((error: unknown) => setTaskError(messageOf(error, 'That did not work.')))
            .finally(() => setTaskBusy(false));
        }}
      />
      {pathDef ? (
        <ConfirmDialog
          open={pendingStage !== null}
          onOpenChange={(open) => {
            if (!open) setPendingStage(null);
          }}
          title={
            pendingStage === CLOSED
              ? `Close this ${base.singular.toLowerCase()}?`
              : `Change the ${pathDef.noun} to ${pathDef.steps.find((s) => s.key === pendingStage)?.label ?? pendingStage}?`
          }
          description={
            pendingStage === CLOSED ? (
              <OptionSelect
                aria-label="Outcome"
                options={offeredOutcomes(pathDef).map((o) => ({ value: o.value, label: o.label }))}
                value={closedOutcome}
                onValueChange={setClosedOutcome}
              />
            ) : undefined
          }
          {...(needsDealRetro
            ? { impact: <InlineMessage tone="warning" role="status">{DEAL_RETRO_REQUIRED}</InlineMessage> }
            : stageError
              ? { impact: <InlineMessage tone="error" role="alert">{stageError}</InlineMessage> }
              : {})}
          confirmLabel={needsDealRetro ? 'Add Deal Retro' : `Update ${pathDef.noun}`}
          busy={stageBusy}
          onConfirm={() => {
            if (!needsDealRetro) {
              void changeStage();
              return;
            }
            // The Deal Retro form, then this confirm again with the outcome chosen.
            setResumeClose(closedOutcome);
            setPendingStage(null);
            relatedActions('deal-retros')?.onNew?.();
          }}
        />
      ) : null}
      {isProduct ? (
        <ConfirmDialog
          open={confirmRetire}
          onOpenChange={(open) => { if (!statusBusy) setConfirmRetire(open); }}
          title="Retire this product?"
          description={
            <>
              <strong>{title}</strong> stays on the quotes and assets that have it, but is no longer offered on new quotes.
              Activate it again to sell it.
            </>
          }
          confirmLabel="Retire"
          tone="danger"
          busy={statusBusy}
          onConfirm={() => void changeStatus('retired')}
        />
      ) : null}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete this ${base.singular.toLowerCase()}?`}
        description={
          <>
            <strong>{title}</strong> moves to the Recycle Bin. {restoreNote()}
            {deleteError ? <InlineMessage tone="error" role="alert" className="mt-3">{deleteError}</InlineMessage> : null}
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        busy={deleting}
        onConfirm={() => { void remove(); }}
      />
    </PageContent>
  );
}
